import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

const API_URL = 'https://ufb-1.onrender.com/api/inventory';

const InventoryDetails = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stockChanges, setStockChanges] = useState({});
  const [stockErrors, setStockErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const res = await fetch(`${API_URL}/${id}`);
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || 'Failed to fetch item');
        }
        const data = await res.json();
        setItem(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id]);

  const handleStockChange = async (size, e) => {
    e.preventDefault();
    const change = stockChanges[size];
    if (!change) return;

    setStockErrors(prev => ({ ...prev, [size]: null }));
    try {
      const res = await fetch(`${API_URL}/${id}/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          size: size,
          change: Number(change) 
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to update stock');
      }
      
      // Re-fetch the updated item
      const itemRes = await fetch(`${API_URL}/${id}`);
      if (!itemRes.ok) {
        const errorData = await itemRes.json();
        throw new Error(errorData.error || 'Failed to fetch updated item');
      }
      const updatedItem = await itemRes.json();
      
      setItem(updatedItem);
      setStockChanges(prev => ({ ...prev, [size]: 0 }));
    } catch (err) {
      setStockErrors(prev => ({ ...prev, [size]: err.message }));
    }
  };

  // Helper function to format dates safely
  const formatDate = (dateField) => {
    if (!dateField) return 'N/A';
    
    try {
      // Check if it's a Firestore timestamp object with _seconds and _nanoseconds
      if (dateField && typeof dateField._seconds === 'number') {
        const milliseconds = dateField._seconds * 1000 + (dateField._nanoseconds || 0) / 1000000;
        return new Date(milliseconds).toLocaleDateString();
      }
      // Check if it's a Firestore timestamp (has toDate method)
      else if (dateField && typeof dateField.toDate === 'function') {
        return new Date(dateField.toDate()).toLocaleDateString();
      }
      // Check if it's already a Date object or date string
      else if (dateField) {
        return new Date(dateField).toLocaleDateString();
      }
      return 'N/A';
    } catch (err) {
      console.error('Date formatting error:', err);
      return 'N/A';
    }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="text-xl">Loading...</div></div>;
  if (error) return <div className="text-red-500 text-center">{error}</div>;
  if (!item) return <div className="text-center">Item not found.</div>;

  const totalStock = Object.values(item.stockBySize || {}).reduce((sum, stock) => sum + stock, 0);
  const sizes = Object.keys(item.stockBySize || {});

  return (
    <div className="max-w-4xl mx-auto p-10 bg-white rounded-2xl shadow-2xl mt-10 border border-blue-200">
      <h2 className="text-3xl font-extrabold mb-6 text-center text-blue-900 tracking-tight">
        {item.productName} - Product Details
      </h2>
      
      {/* Product Info */}
      <div className="mb-8 p-6 bg-blue-50 rounded-xl">
        <div className="flex items-center gap-6">
          {item.imageUrl && (
            <img 
              src={item.imageUrl} 
              alt={item.productName}
              className="w-24 h-24 object-cover rounded-lg border"
            />
          )}
          <div>
            <h3 className="text-2xl font-bold text-blue-900">{item.productName}</h3>
            <p className="text-blue-700">{item.gsm}</p>
            <p className="text-lg font-semibold text-green-700">Total Stock: {totalStock}</p>
            <p className="text-sm text-gray-500">Created: {formatDate(item.createdAt)}</p>
            <p className="text-sm text-gray-500">Updated: {formatDate(item.updatedAt)}</p>
          </div>
        </div>
      </div>

      {/* Sizes List */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Size Details</h3>
        {sizes.length === 0 ? (
          <div className="text-center text-gray-500 py-8">No sizes available for this product.</div>
        ) : (
          sizes.map(size => {
            const currentStock = item.stockBySize[size] || 0;
            const threshold = item.stockThresholds?.[size] || 0;
            const isLowStock = threshold > 0 && currentStock <= threshold;
            
            return (
              <div key={size} className={`flex items-center justify-between p-4 rounded-lg ${isLowStock ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-4">
                  <span className="text-lg font-semibold text-gray-800">{size}</span>
                  <div className="flex flex-col">
                    <span className={`text-xl font-bold ${isLowStock ? 'text-red-700' : 'text-green-700'}`}>
                      {currentStock} in stock
                    </span>
                    {threshold > 0 && (
                      <span className={`text-sm ${isLowStock ? 'text-red-600' : 'text-gray-600'}`}>
                        Threshold: {threshold}
                        {isLowStock && (
                          <span className="ml-2 text-red-600 font-semibold">⚠️ Low Stock Alert!</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                
                <form onSubmit={(e) => handleStockChange(size, e)} className="flex items-center gap-3">
                  <input 
                    type="number" 
                    value={stockChanges[size] || ''} 
                    onChange={(e) => setStockChanges(prev => ({ ...prev, [size]: e.target.value }))}
                    placeholder="Change stock" 
                    className="border-2 border-green-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 bg-white w-32"
                  />
                  {stockErrors[size] && <div className="text-red-500 text-sm">{stockErrors[size]}</div>}
                  <button 
                    type="submit" 
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                  >
                    Update
                  </button>
                </form>
              </div>
            );
          })
        )}
      </div>

      <div className="mt-8 flex gap-4">
        <button onClick={() => navigate(-1)} className="bg-gray-200 px-8 py-2 rounded-lg font-semibold hover:bg-gray-300 transition text-gray-700">
          Back
        </button>
        <Link 
          to={`/edit/${item.id}`}
          className="bg-yellow-400 text-yellow-900 px-8 py-2 rounded-lg font-semibold hover:bg-yellow-500 transition"
        >
          Edit Product
        </Link>
      </div>
    </div>
  );
};

export default InventoryDetails;